"use client";

import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Mic,
  Briefcase,
  Compass,
  DollarSign,
  Send,
  ArrowRight,
} from "lucide-react";
import { useTranslations } from "next-intl";

interface ToolItem {
  key: string;
  icon: React.ElementType;
  inputOutput: string;
  badgeColor: string;
}

export function Features() {
  const t = useTranslations("HomePage.Features");

  const tools: ToolItem[] = [
    {
      key: "resumeGenerator",
      icon: FileText,
      inputOutput: "PDF / LinkedIn → ATS-optimized resume in ~30s",
      badgeColor: "bg-[#FF6B3D]/15 text-[#FF6B3D] border-[#FF6B3D]/30",
    },
    {
      key: "mockInterview",
      icon: Mic,
      inputOutput: "Target Role → Real-time voice practice & clarity score",
      badgeColor: "bg-[#14B8A6]/15 text-[#14B8A6] border-[#14B8A6]/30",
    },
    {
      key: "jobMatcher",
      icon: Briefcase,
      inputOutput: "Skill Profile → Matches from Hahu, Ethiojobs & Afriwork",
      badgeColor: "bg-[#4B3FD1]/15 text-[#4B3FD1] border-[#4B3FD1]/30",
    },
    {
      key: "aiRoleTransitionCoach",
      icon: Compass,
      inputOutput: "Current Role → 90-day skill gap roadmap",
      badgeColor: "bg-[#FF6B3D]/15 text-[#FF6B3D] border-[#FF6B3D]/30",
    },
    {
      key: "marketInsights",
      icon: DollarSign,
      inputOutput: "Job Title & Country → Min/Avg/Max salary + AI boss practice",
      badgeColor: "bg-[#14B8A6]/15 text-[#14B8A6] border-[#14B8A6]/30",
    },
    {
      key: "coverLetterCreator",
      icon: Send,
      inputOutput: "Job Posting → Role-tailored cover letter in seconds",
      badgeColor: "bg-[#4B3FD1]/15 text-[#4B3FD1] border-[#4B3FD1]/30",
    },
  ];

  const gridRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (gridRef.current) {
      observer.observe(gridRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-background via-muted/30 to-background px-6 py-20 md:px-12 md:py-32">
      <div className="mx-auto max-w-6xl">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-balance text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            {t("title")}{" "}
            <span className="bg-gradient-to-r from-[#FF6B3D] via-[#14B8A6] to-[#4B3FD1] bg-clip-text text-transparent animate-gradient-loop">
              {t("titleHighlight")}
            </span>
          </h2>
          <p className="mx-auto max-w-2xl text-pretty text-base text-muted-foreground sm:text-lg leading-relaxed">
            {t("description")}
          </p>
        </div>

        {/* 3D Cursor-Tracked Staggered Feature Cards */}
        <div ref={gridRef} className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {tools.map((tool, index) => (
            <FeatureCard
              key={tool.key}
              tool={tool}
              title={t(`items.${tool.key}.title`)}
              description={t(`items.${tool.key}.description`)}
              index={index}
              inView={inView}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({
  tool,
  title,
  description,
  index,
  inView,
}: {
  tool: ToolItem;
  title: string;
  description: string;
  index: number;
  inView: boolean;
}) {
  const Icon = tool.icon;
  const cardRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    const tiltX = -(y / (rect.height / 2)) * 6; // max 6deg
    const tiltY = (x / (rect.width / 2)) * 6;
    setTilt({ x: tiltX, y: tiltY });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="transition-all duration-700 ease-out"
      style={{
        transform: inView
          ? `perspective(800px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateY(0)`
          : "translateY(30px)",
        opacity: inView ? 1 : 0,
        transitionDelay: `${index * 80}ms`,
      }}
    >
      <Card className="group relative overflow-hidden border-border/80 bg-card/80 backdrop-blur-md p-6 h-full flex flex-col justify-between transition-all duration-300 hover:border-[#FF6B3D]/50 hover:shadow-xl hover:shadow-[#FF6B3D]/10">
        <div>
          {/* Icon with micro hover pulse */}
          <div className="mb-5 inline-flex rounded-xl p-3 bg-muted border border-border/60 text-foreground group-hover:scale-110 group-hover:bg-[#FF6B3D]/10 group-hover:text-[#FF6B3D] transition-all duration-300">
            <Icon className="h-6 w-6 transition-transform group-hover:rotate-6" />
          </div>

          <h3 className="mb-2 text-xl font-bold text-card-foreground group-hover:text-[#FF6B3D] transition-colors">
            {title}
          </h3>

          <p className="text-sm leading-relaxed text-muted-foreground mb-4">
            {description}
          </p>
        </div>

        {/* Input -> Output Concrete Capability Badge */}
        <div className="pt-4 border-t border-border/50">
          <Badge
            variant="outline"
            className={`w-full justify-start text-[11px] font-medium py-1.5 px-2.5 rounded-lg border ${tool.badgeColor}`}
          >
            <ArrowRight className="h-3 w-3 mr-1.5 shrink-0" />
            <span className="truncate">{tool.inputOutput}</span>
          </Badge>
        </div>

        {/* Coral Gradient Hover Overlay */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#FF6B3D]/5 via-transparent to-[#4B3FD1]/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      </Card>
    </div>
  );
}
